import axios from "axios";
import { isBlank } from "../StringUtils";

export const cloudApi = {
	// api_url: "https://fb9f571quc.execute-api.eu-west-1.amazonaws.com/Prod/",
	api_url: "https://api.cloud.byteflies.net/",
	token: "",
};

export interface GetDockStatusDotsResponse {
	position: number;
	dotId?: string;
}

export type GetDockStatusLinkedDotsResponse = {
	dotId: string;
};

export interface GetDockStatusResponse {
	dockId: string;
	groupId: string;
	status: "online" | "offline";
	firmwareVersion?: string;
	desiredFirmwareVersion?: string;
	lastActivity?: number;
	/**
	 * The current battery level, in percent
	 */
	batteryLevel?: number;
	dots: GetDockStatusDotsResponse[];
	linkedDots?: GetDockStatusLinkedDotsResponse[];
}

export interface Group {
	id: string;
	name: string;
	description?: string;
}

export type SignalType =
	| "ACC"
	| "EEG"
	| "EVENT"
	| "ECG"
	| "EMG"
	| "BAT"
	| "GYR"
	| "LEAD_OFF";

export interface ListRecordingSignalResult {
	id: string;
	rawData: string;
	type: SignalType;
}
export interface ListRecordingResult {
	id: string;
	dockName: string;
	dotId: string;
	startDate: number;
	uploadDate: number; //1636453372.728
	duration: number;
	groupId: string;
	patient?: string;
	signals: ListRecordingSignalResult[];
	status: "done";
	stopReason?: string;
}
export interface GetRecordingSignalAlgorithmResult {
	added: number; //1636454192
	id: string;
	type: "EEG_ARTFCTS" | "ECG_ARTFCTS";
}
export interface GetRecordingSignalResult {
	algorithms: GetRecordingSignalAlgorithmResult[];
	channel?: number;
	conversionFactor?: number;
	id: string;
	quality: "CHECK" | "FAIL";
	rawData: string;
	samplingRate: number;
	type: SignalType;
}
export interface GetRecordingResult {
	id: string;
	dockName: string;
	dotId: string;
	firmwareVersion: string;
	startDate: number;
	uploadDate: number; //1636453372.728
	duration: number;
	groupId: string;
	patient?: string;
	signals: GetRecordingSignalResult[];
	status: "done";
	stopReason?: string;
}

export const cloudSetToken = (token: string) => {
	cloudApi.token = token;
};

export const cloudFetchDockStatus = async (
	dockId: string,
): Promise<GetDockStatusResponse | undefined> => {
	if (isBlank(dockId)) {
		throw new Error("dockId is empty");
	}
	const token = cloudApi.token;
	if (isBlank(token) || isBlank(cloudApi.api_url)) {
		throw new Error("Not logged in");
	}
	const instance = axios.create({
		baseURL: cloudApi.api_url,
		timeout: 5000,
		adapter: require("axios/lib/adapters/http"),
		headers: {
			Authorization: token,
			"content-type": "application/json",
		},
	});

	const url = `/docks/${dockId}/status`;
	const response = await instance.get<GetDockStatusResponse>(url);
	const dockStatus = response.data;
	if (dockStatus !== undefined) {
		return dockStatus;
	}
	return undefined;
};

export function cloudRecordingUrl(groupId: string, recordingId: string) {
	return `https://cloud.byteflies.net/groups/${groupId}/recordings/${recordingId}`;
}

export const listRecordings = async (
	groupId: string,
	begin: Date,
	end: Date,
	dotId?: string,
) => {
	if (begin === undefined) {
		throw new Error("begin is not defined");
	} else if (end === undefined) {
		throw new Error("end is not defined");
	} else if (isBlank(groupId)) {
		throw new Error("groupId is not defined");
	}

	const token = cloudApi.token;
	if (isBlank(token) || isBlank(cloudApi.api_url)) {
		throw new Error("Not logged in");
	}
	const instance = axios.create({
		baseURL: cloudApi.api_url,
		timeout: 10000,
		adapter: require("axios/lib/adapters/http"),
		headers: {
			Authorization: token,
			"content-type": "application/json",
		},
	});

	const url = `/groups/${groupId}/recordings`;
	const response = await instance.get<ListRecordingResult[]>(url, {
		params: {
			begin: Math.floor(begin.getTime() / 1000),
			end: Math.ceil(end.getTime() / 1000),
			dot: dotId,
		},
	});

	return response.data;
};

export const getRecording = async (groupId: string, recordingId: string) => {
	if (isBlank(groupId)) {
		throw new Error("groupId is not defined");
	} else if (isBlank(recordingId)) {
		throw new Error("recordingId is not defined");
	}

	const token = cloudApi.token;
	if (isBlank(token) || isBlank(cloudApi.api_url)) {
		throw new Error("Not logged in");
	}
	const instance = axios.create({
		baseURL: cloudApi.api_url,
		timeout: 10000,
		adapter: require("axios/lib/adapters/http"),
		headers: {
			Authorization: token,
			"content-type": "application/json",
		},
	});

	const url = `/groups/${groupId}/recordings/${recordingId}`;
	const response = await instance.get<GetRecordingResult>(url);
	return response.data;
};

export const removeDockFromGroup = async (groupId: string, dockId: string) => {
	const token = cloudApi.token;
	if (isBlank(token) || isBlank(cloudApi.api_url)) {
		throw new Error("Not logged in");
	}

	const instance = axios.create({
		baseURL: cloudApi.api_url,
		timeout: 5000,
		adapter: require("axios/lib/adapters/http"),
		headers: {
			Authorization: token,
			"content-type": "application/json",
		},
	});
	const url = `/groups/${groupId}/docks/${dockId}`;
	await instance.delete<string>(url, {
		headers: { Authorization: token },
	});
};

export default cloudApi;
